import type { EChartsOption } from 'echarts';
import type { TitleOption } from 'echarts/types/dist/shared';


interface IDefaults {
    options: EChartsOption,
    title: TitleOption,
    xAxis: object,
    yAxis: object,
    angleAxis: object,
    parallelAxis: object,
    radiusAxis: object,
    singleAxis: object,
    radarAxis: object
}

export const defaults: IDefaults = {
    options: {
        title: {
            text: "Chart Title",
            subtext: "Subtitle",
            left: "center",  // 'left', 'center', 'right', or percentage
            top: "top",      // 'top', 'middle', 'bottom', or percentage
            textStyle: {
                color: "#333",
                fontSize: 18,
                fontWeight: "bold",
                fontFamily: "Arial"
            }
        },
        legend: {
            show: true,
            type: "plain",  // 'plain', 'scroll'
            orient: "horizontal", // 'horizontal', 'vertical'
            left: "center",
            top: "bottom",
            data: []
        },
        tooltip: {
            show: true,
            trigger: "item",  // 'item', 'axis', 'none'
            formatter: "{a} <br/>{b} -> {c}",
            backgroundColor: "rgba(50,50,50,0.7)",
            borderColor: "#333",
            borderWidth: 0,
            padding: 5,
            textStyle: {
                color: "#fff"
            }
        },
        toolbox: {
            show: true,
            feature: {
                saveAsImage: { "show": true },
                dataView: { "show": true },
                restore: { "show": true },
                dataZoom: { "show": true },
                magicType: {
                    show: true,
                    type: ["line", "bar", "stack"]
                }
            }
        },
        grid: {
            left: "3%",
            right: "4%",
            bottom: "3%",
            containLabel: true
        }
    },
    title: {
        text: "",
        subtext: "",
        left: "center",  // 'left', 'center', 'right', or percentage
        top: "top",      // 'top', 'middle', 'bottom', or percentage
        textStyle: {
            color: "#333",
            fontSize: 18,
            fontWeight: "bold",
            fontFamily: "Arial"
        }
    },
    xAxis: {
        type: 'category',
        name: '',
        nameLocation: 'end',
        nameGap: 15,
        boundaryGap: true,
        min: null,
        max: null,
        scale: false,
        splitNumber: 5,
        position: 'bottom',
        offset: 0,
        axisLine: {
            show: true,
            onZero: true,
            lineStyle: {
                color: '#333',
                width: 1
            }
        },
        axisTick: {
            show: true,
            alignWithLabel: false,
            length: 5
        },
        axisLabel: {
            show: true,
            rotate: 0,
            formatter: null
        },
        splitLine: {
            show: false,
            lineStyle: {
                color: '#ccc',
                width: 1
            }
        },
        data: []
    },
    yAxis: {
        type: 'value',
        name: '',
        nameLocation: 'end',
        nameGap: 15,
        inverse: false,
        min: null,
        max: null,
        scale: false,
        splitNumber: 5,
        position: 'left',
        offset: 0,
        axisLine: {
            show: true,
            onZero: true,
            lineStyle: {
                color: '#333',
                width: 1
            }
        },
        axisTick: {
            show: true,
            alignWithLabel: false,
            length: 5
        },
        axisLabel: {
            show: true,
            formatter: null
        },
        splitLine: {
            show: true,
            lineStyle: {
                color: '#ccc',
                width: 1
            }
        },
        data: []
    },
    angleAxis: {
        type: "value",
        startAngle: 0,
        min: 0,
        max: 360
    },
    radiusAxis: {
        type: "value",
        min: 0,
        max: 100
    },
    parallelAxis: [
    {
        dim: 0,
        name: "Dimension 1",
        type: "value"
    }, 
    {
        dim: 1,
        name: "Dimension 2",
        type: "category",
        data: []
    }],
    singleAxis: {
        type: "value",
        position: "bottom",
        width: "80%"
    },
    radarAxis: {
        indicator: [],          // to be set in the web component
        shape: 'polygon',
        radius: '75%',
        startAngle: 90,
        splitNumber: 5,
        scale: false,
        center: ['50%', '50%'],
        axisName: {
            show: true,
            formatter: '{value}',
            color: '#666',
            fontSize: 12,
            padding: [3, 5]
        },
        splitLine: {
            show: true,
            lineStyle: {
                color: ['#ddd'],
                width: 1,
                type: 'solid'
            }
        },
        splitArea: {
            show: true,
            areaStyle: {
                color: ['rgba(250,250,250,0.3)', 'rgba(200,200,200,0.3)'],
                opacity: 1
            }
        },
        axisLine: {
            show: true,
            splitNumber: 5,
            lineStyle: {
                color: '#ddd',
                width: 1,
                type: 'solid'
            }
        }
    }
}

export const configure = (type: string) => {
    console.log('configuring :', type)
    var config: EChartsOption = {... defaults.options}
    switch(type.toLowerCase()) {
        case 'line':
            config.xAxis = {...defaults.xAxis}
            config.yAxis = {...defaults.yAxis}
            config.series = [{
                type: 'line',
                name: '',                         // to be set in web component
                data: [],
                // stack: 'Total',
                smooth: true,
                smoothMonotone: 'x',
                symbol: 'circle',
                symbolSize: 6,
                showSymbol: true,
                step: false,
                areaStyle: {
                    color: 'rgba(58,65,93,0.3)',
                    opacity: 0.8,
                    origin: 'auto'
                },
                lineStyle: {
                    color: '#3a415d',
                    width: 2,
                    type: 'solid',
                    opacity: 1
                },
                // itemStyle: {
                //     color: '#3a415d',
                //     borderColor: '#fff',
                //     borderWidth: 1,
                //     borderType: 'solid',
                //     opacity: 1
                // },
                label: {
                    show: false,
                    position: 'top',
                    formatter: '{c}',
                    color: '#333',
                    fontSize: 12
                },
                emphasis: {
                    scale: true,
                    focus: 'series',
                    itemStyle: {
                        color: '#3a415d'
                    },
                    label: {
                        show: true,
                        position: 'top'
                    }
                },
                markPoint: {
                    data: [
                        { type: 'max', name: 'Maximum' },
                        { type: 'min', name: 'Minimum' }
                    ]
                },
                markLine: {
                    data: [
                        { type: 'average', name: 'Moyenne' }
                    ]
                },
                // markArea: {
                //     data: [[{
                //         name: 'Area',
                //         x: '10%',
                //         y: '10%'
                //     }, 
                //     {
                //         x: '20%',
                //         y: '20%'
                //     }]]
                // },
                sampling: 'average',
                dimensions: ['date', 'value'],
                encode: {
                    x: 0,
                    y: 1
                },
                animation: true,
                animationThreshold: 2000,
                animationDuration: 1000,
                animationEasing: 'cubicOut',
                animationDelay: 0
            }]
            // config.dataZoom = [{
            //     type: 'slider',
            //     show: true,
            //     start: 0,
            //     end: 100,
            //     orient: 'horizontal',
            //     zoomLock: false,
            //     throttle: 100
            // }, 
            // {
            //     type: 'inside',
            //     start: 0,
            //     end: 100
            // }]
            break
        case 'bar':
            config.xAxis = {...defaults.xAxis}
            config.yAxis = {...defaults.yAxis}
            config.series = [ { type: 'bar', data: [] } ]
            config.series = [{
                type: 'bar',
                name: '',                               // to be set in web component
                // stack: 'Total',
                barWidth: '60%',
                barMaxWidth: 50,
                barGap: '30%',
                barCategoryGap: '20%',
                data: [120, 200, 150, 80, 70, 110, 130],
                coordinateSystem: 'cartesian2d',
                showBackground: true,
                backgroundStyle: {
                    color: 'rgba(180, 180, 180, 0.2)',
                    borderColor: '#ccc',
                    borderWidth: 1,
                    borderType: 'solid',
                    borderRadius: [0, 0, 0, 0],
                    opacity: 1
                },
                // itemStyle: {
                //     color: '#3a415d',
                //     borderColor: '#fff',
                //     borderWidth: 1,
                //     borderType: 'solid',
                //     borderRadius: [4, 4, 0, 0],
                //     opacity: 1
                // },
                label: {
                    show: false,
                    position: 'top',
                    distance: 5,
                    rotate: 0,
                    offset: [0, 0],
                    formatter: '{c}',
                    color: '#333',
                    fontSize: 12
                },
                emphasis: {
                    focus: 'series',
                    blurScope: 'global',
                    itemStyle: {
                        color: '#5470c6',
                        borderColor: '#fff',
                        borderWidth: 1,
                        borderType: 'solid',
                        borderRadius: [4, 4, 0, 0],
                        opacity: 1
                    },
                    label: {
                        show: true,
                        position: 'top',
                        formatter: '{c}',
                        fontSize: 14
                    }
                },
                markPoint: {
                    data: [
                        { type: 'max', name: 'Maximum', label: {formatter: '{b}: {c}'} },   // {b} is name, {c} is value
                        { type: 'min', name: 'Minimum', label: {formatter: '{b}: {c}'} },   // {b} is name, {c} is value
                    ]
                },
                markLine: {
                    data: [
                        { type: 'average', name: 'Average' }
                    ]
                },
                // markArea: {
                //     data: [[
                //         { type: 'min' },
                //         { type: 'max' }
                //     ]]
                // },
                // dimensions: ['category', 'value'],
                // encode: {
                //     x: 0,
                //     y: 1
                // },
                animation: true,
                animationThreshold: 2000,
                animationDuration: 1000,
                animationEasing: 'cubicOut',
                animationDelay: 0
            }]
            // config.dataZoom = [{
            //     type: 'slider',
            //     show: true,
            //     start: 0,
            //     end: 100,
            //     orient: 'horizontal'
            // }, 
            // {
            //     type: 'inside',
            //     start: 0,
            //     end: 100
            // }]
            break
        case 'scatter':
            config.xAxis = {}//{...defaults.xAxis}
            config.yAxis = {}//{...defaults.yAxis}
            config.series = [ { type: 'scatter', symbolSize: 20, data: [] } ]
            break
        case 'pie':
            console.log('I am configuring pie chart')
            config.series = [{
                type: 'pie',
                name: '',
                radius: '50%',
                center: '50%',
                avoidLabelOverlap: true,
                selectedMode: 'single',
                selectedOffset: 10,
                clockwise: true,
                startAngle: 90,
                minAngle: 0,
                minShowLabelAngle: 0,
                animation: true,
                animationThreshold: 2000,
                animationDuration: 1000,
                animationEasing: 'cubicOut',
                animationDelay: 0,
                label: {
                    show: true,
                    position: 'outside',
                    formatter: '{b}: {c}',
                    color: '#666',
                    fontSize: 12,
                    fontWeight: 'normal',
                    lineHeight: 20,
                    backgroundColor: 'transparent',
                    borderColor: 'transparent',
                    borderWidth: 0,
                    padding: [0, 0],
                    borderRadius: 4
                },
                labelLine: {
                    show: true,
                    length: 15,
                    length2: 15,
                    smooth: false,
                    lineStyle: {
                    color: '#ccc',
                    width: 1,
                    type: 'solid'
                    }
                },
                itemStyle: {
                    borderColor: '#fff',
                    borderWidth: 1,
                    borderType: 'solid',
                    borderRadius: 0,
                    opacity: 1
                },
                emphasis: {
                    scale: true,
                    scaleSize: 10,
                    focus: 'self',
                    blurScope: 'global',
                    label: {
                    show: true,
                    fontSize: 14,
                    fontWeight: 'bold'
                    }
                },
                data: []
            }]
            break
        case 'radar':
            config.radar = {... defaults.radarAxis}
            config.series = [{
                type: 'radar',
                name: '',         // to be set in the web component
                data: [],
                symbol: 'circle',
                symbolSize: 6,
                symbolRotate: 0,
                label: {
                    show: false,
                    formatter: '{c}',
                    position: 'top'
                },
                itemStyle: {
                    color: '#67c23a',
                    borderColor: '#fff',
                    borderWidth: 1,
                    borderType: 'solid',
                    opacity: 1
                },
                lineStyle: {
                    color: '#67c23a',
                    width: 2,
                    type: 'solid'
                },
                areaStyle: {
                    color: 'rgba(103,194,58,0.4)',
                    opacity: 0.9
                },
                emphasis: {
                    itemStyle: {
                      color: '#67c23a'
                },
                lineStyle: {
                      width: 3
                },
                areaStyle: {
                      opacity: 0.8
                    }
                },
                animation: true,
                animationThreshold: 2000,
                animationDuration: 1000,
                animationEasing: 'cubicOut',
                animationDelay: 0
            }]
            break
        case 'heatmap':
        case 'tree':
        case 'treemap':
        case 'sunburst':
        case 'boxplot':
        case 'canlestick':
        case 'funnel':
        case 'gauge':
            config.series = [{
                type: 'gauge',
                name: 'Progress',
                min: -100,
                max: 100,
                splitNumber: 10,
                radius: '75%',
                center: ['50%', '50%'],
                startAngle: 225,
                endAngle: -45,
                clockwise: true,
                data: [],
                axisLine: {
                    show: true,
                    lineStyle: {
                        color: [
                            [0.3, '#BB0000'],
                            [0.7, '#B7B6B6'],
                            [1, '#07AD02']
                        ],
                        width: 30,
                        opacity: 1
                    }
                },
                progress: {
                    show: true,
                    width: 8,
                    itemStyle: {
                        color: '#37a2da',
                        opacity: 1
                    },
                    roundCap: true
                },
                splitLine: {
                    show: true,
                    length: 15,
                    distance: 10,
                    lineStyle: {
                        color: '#999',
                        width: 2,
                        type: 'solid'
                    }
                },
                axisTick: {
                    show: true,
                    splitNumber: 5,
                    length: 6,
                    distance: 10,
                    lineStyle: {
                        color: '#999',
                        width: 1
                    }
                },
                axisLabel: {
                    show: true,
                    distance: 25,
                    color: '#999',
                    fontSize: 12,
                    formatter: '{value}'
                },
                pointer: {
                    show: true,
                    length: '60%',
                    width: 6,
                    itemStyle: {
                        color: '#333',
                        opacity: 1
                    }
                },
                anchor: {
                    show: true,
                    showAbove: true,
                    size: 18,
                    icon: 'circle',
                    offsetCenter: [0, 0],
                    itemStyle: {
                        color: '#fff',
                        borderColor: '#333',
                        borderWidth: 2,
                        opacity: 1
                    }
                },
                title: {
                    show: true,
                    offsetCenter: [0, '20%'],
                    color: '#333',
                    fontSize: 14
                },
                detail: {
                    show: true,
                    offsetCenter: [0, '40%'],
                    color: '#333',
                    fontSize: 14,
                    formatter: '{value}%',
                    valueAnimation: true
                },
                animation: true,
                animationThreshold: 2000,
                animationDuration: 1000,
                animationEasing: 'cubicOut',
                animationDelay: 0
            }]
            break;
        case 'graph':
        case 'parallel':
        case 'sankey':
        case 'themeriver':
        case 'polar':
        default:
            break
    }
    return config
}

export const setData = (input: any[], options: EChartsOption) => {
    console.log('SET DATA :', input?.length, input, options)
    var type = Array.isArray(options.series) ? options.series[0].type : undefined
    input = Array.isArray(input) ? input : [input] 
    switch(type) {
        case 'line':
        case 'bar':
            // input = { key: value, key: value, ... }
            // @ts-ignore
            let items: number[] = [] //options.xAxis?.data || options.yAxis?.data
            console.log(type, 'items -->', items)
            if(!items?.length) {
                // @ts-ignore
                items = Object.keys(input[0])
                console.log('no items ==>', items, 'data :', input[0])
                // @ts-ignore
                options.xAxis.data = items
            }
            if(Array.isArray(options.series))
                options.series.forEach( (series, index) => { series.data = items.map( item => parseFloat(input[index][item])||0 ) } )
            // console.log(options.series?.map(s => s.data))
            break
        case 'scatter':
            // input = [[x, y], [x, y], ...]
            if(Array.isArray(options.series))
                options.series.forEach( (series) => { series.data = input } )
            break
        case 'pie':
            console.log('PIE CHART SERIES', options.series)
            // input = { key: value, key: value, ... }
            if(Array.isArray(options.series))
                options.series.forEach( (series, index) => { series.data = Object.keys(input[index]).map( key => ({ name: key, value: input[index][key] }) ) } )
            break
        case 'radar':
            // input = { item: { key: value, key: value, ... } }
            console.log('draw radar', input, options.series)
            console.log('radar', options.radar)
            let radarOptions = Array.isArray(options.radar)? options.radar[0] : options.radar
            if(!radarOptions?.indicator?.length) {
                let first = Object.values(input[0])[0] as object | {}
                let indic = Object.keys(first)
                let value = Object.values(first)
                let maxim = Math.max(...value) * 1.5
                console.log('first', first)
                console.log('indic', indic)
                console.log('value', value)
                console.log('maxim', maxim)
                options.radar = { indicator: indic.map( ind => ({ name: ind, max: maxim })) }
            }
            // options.radar = {
            //     indicator: [
            //         {name: "Happiness", max: 200}, 
            //         {name: "Sadness", max: 200},
            //         {name: "Anger", max: 200},
            //         {name: "Fear", max: 200},
            //         {name: "Neutral", max: 200}
            //     ]
            // }
            if(Array.isArray(options.series)) {
                options.series.forEach( (series, index) => { 
                    series.data = Object.keys(input[index]).map( item => ( { 
                        name: item,
                        value: Object.keys(input[index][item]).map( key => input[index][item][key])
                    } ) )
                    console.log('series.data =', series.data)
                } )
            }
            break
        case 'heatmap':
        case 'tree':
        case 'treemap':
        case 'sunburst':
        case 'boxplot':
        case 'candlestick':
        case 'funnel':
        case 'gauge':
            // input = { key: value }
            console.log('set data for Gauge', input)
            if(Array.isArray(options.series))
                options.series.forEach( (series, index) => { series.data = Object.keys(input[index]).map( key => ({ name: key, value: input[index][key] }) ) } )
            break;
        case 'graph':
        case 'parallel':
        case 'sankey':
        case 'themeRiver':
        default:
            break
    }
    return options
}

